import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { TrendingUp, Target, Users, Award, Lightbulb, Globe } from 'lucide-react';

const WhoWeAre = () => {
  const values = [
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'Spirited Vision',
      description: 'Courage to imagine a radically different and prosperous future for Africa',
      example: '1GW Solar Initiative',
      slug: '1gw-solar-initiative'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Intelligent Strategy',
      description: 'Data-driven, analytically rigorous approach to every challenge',
      example: 'AI Logistics Models',
      slug: 'ai-logistics'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Synergistic Leadership',
      description: 'Building powerful alliances across all sectors for mutual benefit',
      example: '10 UN SDGs Advanced',
      slug: 'un-sdgs'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Innovative Action',
      description: 'Relentless focus on translating vision into measurable reality',
      example: 'Pan-African EV Network',
      slug: 'ev-network'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Service Excellence',
      description: 'Uncompromising commitment to quality in every interaction',
      example: 'ESG Gold Standard',
      slug: 'esg-standard'
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: 'Continental Impact',
      description: 'Creating lasting change across Africa and beyond',
      example: '12,750 Veterans Empowered',
      slug: 'veteran-empowerment'
    }
  ];

  const galleryImages = [
    { src: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?q=85&w=800', label: 'Utility-Scale Solar', slug: '1gw-solar-initiative' },
    { src: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?q=85&w=800', label: 'Climate-Smart Agriculture', slug: 'veteran-empowerment' },
    { src: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=85&w=800', label: 'Deep Technology', slug: 'ai-logistics' },
    { src: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?q=85&w=800', label: 'Smart Cities', slug: 'smart-cities-reit' }
  ];

  const leaders = [
    {
      name: 'Trevor Harrison',
      role: 'Chairman & GCEO',
      image: 'https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/v0thrzyc_IMG-20250919-WA0035.jpg',
      bio: 'Visionary strategist with expertise in real estate development and financial structuring'
    },
    {
      name: 'Michael Mapfumo',
      role: 'Group COO',
      image: 'https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/1zpe8849_IMG-20250919-WA0010.jpg',
      bio: 'Operations expert in mineral development and resource value chains'
    },
    {
      name: 'Cathrine Chimanda',
      role: 'Board Secretary & CLO',
      image: 'https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/kkn1me1p_IMG-20250919-WA0029.jpg',
      bio: 'Legal expert in international trade and corporate governance'
    },
    {
      name: 'Debra Zoe Mugoto',
      role: 'Chief Financial Officer',
      image: 'https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/w4ey7wqq_IMG-20250919-WA0031.jpg',
      bio: 'Finance executive specializing in DFI funding and PE structuring'
    },
    {
      name: 'Chipo Mugoto',
      role: 'Chief Marketing Officer',
      image: 'https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/zm4kmc4s_IMG-20250919-WA0022.jpg',
      bio: 'Dynamic marketing strategist with expertise in brand development and ESG communications'
    },
    {
      name: 'Ignatius Daiton',
      role: 'Director of Logistics',
      image: 'https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/ngep7blt_IMG-20250919-WA0021.jpg',
      bio: 'Logistics expert pioneering solar-powered transport and EV infrastructure development'
    },
    {
      name: 'Chido Murimi',
      role: 'Chief Communications Officer',
      image: 'https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/d0sw56tj_FB_IMG_1772520784149.jpg',
      bio: 'Creative media professional transforming Xcelsior\'s impact into compelling narratives'
    },
    {
      name: 'Takudzwa Murimi',
      role: 'Chief Technology Officer',
      image: 'https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/xo8g1qnc_IMG-20260303-WA0003.jpg',
      bio: 'Innovative technology leader deploying AI, blockchain, and IoT solutions for African markets'
    },
    {
      name: 'Collins Lloyd Chimamanda',
      role: 'Director of Commodities',
      image: 'https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/1eaomfmp_IMG-20250919-WA0019.jpg',
      bio: 'Expert in agro-value chain development and sustainable mining with blockchain traceability'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1721907758701-d118fdd56b50?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/40"></div>
          {/* Animated overlay pattern */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0, 206, 209, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 140, 0, 0.3) 0%, transparent 50%)',
          }}></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-white">
          <h1 className="text-6xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ascende Superius
          </h1>
          <p className="text-3xl mb-2">Africa's Sustainable Renaissance</p>
          <p className="text-xl mb-8 text-gray-200">Since 2020: Engineering Growth Through ESG Innovation</p>
          <div className="flex gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#FF8C00] hover:bg-[#e07d00] text-white font-semibold px-8 transition-all duration-300 transform hover:scale-105"
            >
              Explore Our Impact
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-[#00CED1] text-white hover:bg-[#00CED1]/20 font-semibold px-8 transition-all duration-300"
              onClick={() => window.open('/xsi-group-profile-2026.pdf', '_blank')}
              data-testid="download-profile-button"
            >
              Download Profile
            </Button>
          </div>
        </div>
      </section>

      {/* Xcelsior Essence */}
      <section className="py-20 px-6 bg-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1644325349124-d1756b79dd42?q=85&w=800')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              The Xcelsior Essence
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-t-4 border-t-[#00CED1] hover:shadow-xl transition-shadow duration-300 overflow-hidden">
              <div className="relative h-40 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=85&w=800" alt="Excelsior - Ever Upward" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardHeader>
                <Link to="/explore/excelsior" className="text-2xl font-bold text-[#00CED1] hover:underline underline-offset-4" style={{ fontFamily: 'Montserrat, sans-serif' }} data-testid="essence-link-excelsior">
                  Excelsior
                </Link>
                <p className="text-gray-600 italic">Latin: Ever Upward</p>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">Our commitment to continuous rise, progress, and transcending limitations</p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#FF8C00] hover:shadow-xl transition-shadow duration-300 overflow-hidden">
              <div className="relative h-40 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=85&w=800" alt="Exousia - Authority and Power" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardHeader>
                <Link to="/explore/exousia" className="text-2xl font-bold text-[#FF8C00] hover:underline underline-offset-4" style={{ fontFamily: 'Montserrat, sans-serif' }} data-testid="essence-link-exousia">
                  Exousia
                </Link>
                <p className="text-gray-600 italic">Greek: Authority & Power</p>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">Our mandate to act with legitimacy, responsibility, and transformative influence</p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#00CED1] hover:shadow-xl transition-shadow duration-300 overflow-hidden">
              <div className="relative h-40 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1573164713988-8665fc963095?q=85&w=800" alt="Eccellenza - Excellence" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardHeader>
                <Link to="/explore/eccellenza" className="text-2xl font-bold text-[#00CED1] hover:underline underline-offset-4" style={{ fontFamily: 'Montserrat, sans-serif' }} data-testid="essence-link-eccellenza">
                  Eccellenza
                </Link>
                <p className="text-gray-600 italic">Italian: Excellence</p>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">Our unwavering pursuit of superior standards in every deliverable</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 mb-8">
            <p className="text-xl text-gray-800 text-center leading-relaxed">
              <Link to="/explore/our-story" className="font-bold text-[#FF8C00] hover:underline underline-offset-4" data-testid="story-link-covid">Established during COVID-19</Link> to drive Africa's sustainable development. 
              We are not just a company—we are <Link to="/explore/our-story" className="font-bold text-[#00CED1] hover:underline underline-offset-4" data-testid="story-link-movement">a movement</Link> dedicated to 
              elevating Africa's industrial and economic destiny.
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-l-4 border-l-[#00CED1] bg-white hover:shadow-2xl transition-shadow duration-300">
              <CardHeader className="bg-[#00CED1] text-white">
                <h3 className="text-3xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Our Vision</h3>
              </CardHeader>
              <CardContent className="p-8">
                <p className="text-xl text-gray-800 leading-relaxed">
                  To be Africa's foremost catalyst for technology-driven, ESG-led industrial transformation
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-[#FF8C00] bg-white hover:shadow-2xl transition-shadow duration-300">
              <CardHeader className="bg-[#FF8C00] text-white">
                <h3 className="text-3xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Our Mission</h3>
              </CardHeader>
              <CardContent className="p-8">
                <p className="text-xl text-gray-800 leading-relaxed">
                  To accelerate Africa's sustainable industrialization through financial innovation, disruptive technology, and scalable infrastructure
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-gradient-to-r from-[#00CED1] to-[#FF8C00] p-1 rounded-lg">
              <div className="bg-white px-12 py-6 rounded-lg">
                <p className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Ascende Superius
                </p>
                <p className="text-xl text-gray-600 italic">Seek Higher Things!</p>
              </div>
            </div>
          </div>

          {/* Xcelsior in Motion Gallery */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <Link key={index} to={`/explore/${img.slug}`} className="group relative h-48 rounded-lg overflow-hidden block" data-testid={`gallery-link-${img.slug}`}>
                <img src={img.src} alt={img.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <p className="absolute bottom-3 left-3 right-3 text-white font-semibold text-sm">{img.label}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our Core Values
            </h2>
            <div className="w-24 h-1 bg-[#FF8C00] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card 
                key={index} 
                className="border-t-4 border-t-[#00CED1] hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-[#00CED1]/10 rounded-full text-[#00CED1]">
                      {value.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {value.title}
                    </h3>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">{value.description}</p>
                  <div className="pt-4 border-t border-gray-200">
                    <Link to={`/explore/${value.slug}`} className="text-sm font-semibold text-[#FF8C00] hover:underline underline-offset-4" data-testid={`value-link-${value.slug}`}>
                      {value.example} →
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Circle */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Leadership Circle
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
            <p className="text-xl text-gray-300">Meet the visionaries driving Africa's sustainable transformation</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {leaders.map((leader, index) => (
              <Card 
                key={index} 
                className="bg-gray-800/50 border border-gray-700 hover:border-[#00CED1] transition-all duration-300 transform hover:scale-105 overflow-hidden"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={leader.image} 
                    alt={leader.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                </div>
                <CardContent className="p-6">
                  <Link to="/explore/leadership" className="text-2xl font-bold text-[#00CED1] mb-2 block hover:underline underline-offset-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {leader.name}
                  </Link>
                  <Link to="/explore/leadership" className="text-[#FF8C00] font-semibold mb-3 block hover:underline underline-offset-4">{leader.role}</Link>
                  <p className="text-gray-300 text-sm">{leader.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Alignment & Impact */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Strategic Alignment
            </h2>
            <div className="w-24 h-1 bg-[#FF8C00] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/explore/zimbabwe-vision-2030" data-testid="alignment-link-2030">
              <Card className="text-center border-2 border-[#00CED1] hover:shadow-xl transition-shadow duration-300 h-full">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-[#00CED1] mb-4">2030</div>
                  <h3 className="text-xl font-bold mb-3">Zimbabwe Vision 2030</h3>
                  <p className="text-gray-600">Renewable energy, food security, infrastructure modernization</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/explore/au-agenda-2063" data-testid="alignment-link-2063">
              <Card className="text-center border-2 border-[#FF8C00] hover:shadow-xl transition-shadow duration-300 h-full">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-[#FF8C00] mb-4">2063</div>
                  <h3 className="text-xl font-bold mb-3">AU Agenda 2063</h3>
                  <p className="text-gray-600">Sustainable cities, integrated transport, agricultural modernization</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/explore/un-sdgs" data-testid="alignment-link-sdgs">
              <Card className="text-center border-2 border-[#00CED1] hover:shadow-xl transition-shadow duration-300 h-full">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-[#00CED1] mb-4">10</div>
                  <h3 className="text-xl font-bold mb-3">UN SDGs</h3>
                  <p className="text-gray-600">Direct impact on 10 Sustainable Development Goals</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeAre;